import { Typography } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { TFunction } from 'i18next'
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import {
  checkIfSubmittable,
  profileInfoValidation,
} from '../../../../utils/validation'
import { useAppState } from '../../../appState'
import closeMenuIcon from '../../../assets/images/close.svg'
import axios from '../../../axios'
import { ENDPOINTS, baseURL } from '../../../constants'
import CHRButton from '../../button'
import CHRInput from '../../input'
import { styles } from './styles'

const parseFirstName = (fullName: string | undefined): string => {
  if (!fullName) return ''
  return fullName.split(' ')[0] || ''
}
const parseLastName = (fullName: string | undefined): string => {
  if (!fullName) return ''
  return fullName.split(' ')[1] || ''
}

interface DetailsModalProps {
  handleClose: any
  openStatus: boolean
  handleFinish: Function
  t: TFunction
}

const DetailsModal = ({
  handleClose,
  openStatus,
  handleFinish,
  t,
}: DetailsModalProps) => {
  const classes = styles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [state, dispatch] = useAppState()
  const [userInfo, setUserInfo] = useState({
    firstName: parseFirstName(state.userProfile.fullName),
    lastName: parseLastName(state.userProfile.fullName),
    userName: state.userProfile.userName || '',
  })

  const [userInfoErrors, setUserInfoErrors] = useState({
    firstNameMsg: '',
    lastNameMsg: '',
    userNameMsg: '',
  })

  const [requestStatus, setRequestStatus] = useState({
    requestSuccessMsg: '',
    requestErrorMsg: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (requestStatus.requestSuccessMsg) {
      setTimeout(() => {
        handleFinish()
      }, 2000)

      dispatch({
        type: 'UPDATE_USER_DATA',
        userProfile: {
          ...state.userProfile,
          userName: userInfo.userName,
          fullName: userInfo.firstName + ' ' + userInfo.lastName,
        },
      })
    }
    return
  }, [requestStatus.requestSuccessMsg])

  const handleTextChange = (event: any, fieldName: string) => {
    setUserInfoErrors({
      firstNameMsg: '',
      lastNameMsg: '',
      userNameMsg: '',
    })
    setUserInfo({ ...userInfo, [fieldName]: event.target.value })
  }

  const handleInfoSubmission = async () => {
    setIsSubmitting(true)
    try {
      const isFormValid = await profileInfoValidation({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        userName: userInfo.userName,
      })

      const userData = {
        name: `${userInfo.firstName} ${userInfo.lastName}`,
        username: userInfo.userName,
      }

      const result = await axios.patch(
        `${baseURL}/${ENDPOINTS.user.replace(':id', state.userId)}`,
        userData
      )

      if (result) {
        setRequestStatus({ requestSuccessMsg: t('messages.yourInfoUpdated') })
      }
      // navigate(ROUTES.personalInfo)
    } catch (error) {
      if (error.errors) {
        const errors = error?.inner?.reduce(
          (accumulator: {}, ele) => {
            accumulator[ele?.path] = ele.message
            return accumulator
          },
          { firstName: '', lastName: '', userName: '' }
        )
        setUserInfoErrors({
          firstNameMsg: errors.firstName,
          lastNameMsg: errors.lastName,
          userNameMsg: errors.userName,
        })
      } else {
        if (error.response?.data?.message === 'username is already exists')
          setUserInfoErrors({ userNameMsg: t(`dashboard.uniqueUsername`) })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={openStatus}
        onClose={handleClose}
        aria-labelledby="personalInfo"
        className={classes.modal}
      >
        <div className={classes.modalContainer}>
          <button onClick={handleClose} className={classes.closeButton}>
            <img src={closeMenuIcon} alt={t('imageAlts.close')} />
          </button>
          <Typography variant="h2" className={classes.modalTitle}>
            your details
          </Typography>
          <Typography variant="subtitle1">
            Grab your username and add a few details and you’re all set to start
            earning cash back!
          </Typography>
          <div className={classes.inputContainer}>
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.errorMsg}
            >
              {userInfoErrors.userNameMsg}
            </Typography>
            <CHRInput
              value={userInfo.userName}
              onChange={() => handleTextChange(event, 'userName')}
              placeholder="choose a username"
              type="text"
              error={userInfoErrors.userNameMsg}
              required
              onKeyPress={event =>
                !isSubmitting && checkIfSubmittable(event, handleInfoSubmission)
              }
            />
          </div>
          <div className={classes.inputContainer}>
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.errorMsg}
            >
              {userInfoErrors.firstNameMsg}
            </Typography>
            <CHRInput
              value={userInfo.firstName}
              onChange={() => handleTextChange(event, 'firstName')}
              placeholder={t('shared.firstName')}
              type="text"
              error={userInfoErrors.firstNameMsg}
              required
              onKeyPress={event =>
                !isSubmitting && checkIfSubmittable(event, handleInfoSubmission)
              }
            />
          </div>
          <div className={classes.inputContainer}>
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.errorMsg}
            >
              {userInfoErrors.lastNameMsg}
            </Typography>
            <CHRInput
              value={userInfo.lastName}
              onChange={() => handleTextChange(event, 'lastName')}
              placeholder={t('shared.lastName')}
              type="text"
              error={userInfoErrors.lastNameMsg}
              required
              onKeyPress={event =>
                !isSubmitting && checkIfSubmittable(event, handleInfoSubmission)
              }
            />
          </div>

          <div className={classes.buttonContainer}>
            <CHRButton
              onClick={handleInfoSubmission}
              label={t('shared.letsGo')}
              isSubmitting={isSubmitting}
              disabled={isSubmitting}
            />
            <Typography
              variant="subtitle1"
              component="p"
              className={classes.successMsg}
            >
              {requestStatus.requestSuccessMsg}
            </Typography>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default withTranslation()(DetailsModal)
